"use client"

import * as React from "react"
import { useLocale, useTranslations } from "next-intl"
import { DayPicker } from "react-day-picker"
import { de, enUS, fr, type Locale } from "date-fns/locale"
import { format, parse, isValid } from "date-fns"
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import "react-day-picker/style.css"

const LOCALE_MAP: Record<string, Locale> = { en: enUS, de, fr }

interface DatePickerProps {
  id?: string
  name: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  toDate?: Date
  fromDate?: Date
}

const DISPLAY_FORMAT = "dd/MM/yyyy"
const ISO_FORMAT = "yyyy-MM-dd"

function toISO(date: Date): string {
  return format(date, ISO_FORMAT)
}

function fromISO(value: string): Date | undefined {
  if (!value) return undefined
  const parsed = parse(value, ISO_FORMAT, new Date())
  return isValid(parsed) ? parsed : undefined
}

function formatAsDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, "")
  let result = ""
  for (let i = 0; i < digits.length && i < 8; i++) {
    if (i === 2 || i === 4) result += "/"
    result += digits[i]
  }
  return result
}

function parseDisplayDate(text: string, min: Date, max: Date): Date | undefined {
  if (text.replace(/\D/g, "").length !== 8) return undefined
  const parsed = parse(text, DISPLAY_FORMAT, new Date())
  if (!isValid(parsed)) return undefined
  if (parsed < min || parsed > max) return undefined
  return parsed
}

export function DatePicker({
  id,
  name,
  value,
  defaultValue,
  onChange,
  disabled,
  placeholder = "dd/mm/yyyy",
  className,
  ariaInvalid,
  ariaDescribedBy,
  toDate,
  fromDate,
}: DatePickerProps) {
  const locale = useLocale()
  const t = useTranslations("auth")
  const dateLocale = LOCALE_MAP[locale] ?? enUS

  const today = React.useMemo(() => new Date(), [])
  const max = toDate ?? today
  const min = React.useMemo(() => {
    if (fromDate) return fromDate
    const d = new Date(today)
    d.setFullYear(d.getFullYear() - 120)
    return d
  }, [fromDate, today])

  const initialDate = fromISO(value ?? defaultValue ?? "")
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(initialDate)
  const [inputValue, setInputValue] = React.useState(
    initialDate ? format(initialDate, DISPLAY_FORMAT) : "",
  )
  const [calendarMonth, setCalendarMonth] = React.useState<Date>(initialDate ?? max)
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (value !== undefined) {
      const d = fromISO(value)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync controlled state with the `value` prop in controlled mode
      setSelectedDate(d)
      setInputValue(d ? format(d, DISPLAY_FORMAT) : "")
      if (d) setCalendarMonth(d)
    }
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatAsDateInput(e.target.value)
    setInputValue(formatted)

    const parsed = parseDisplayDate(formatted, min, max)
    if (parsed) {
      setSelectedDate(parsed)
      setCalendarMonth(parsed)
      onChange?.(toISO(parsed))
    } else if (formatted === "") {
      setSelectedDate(undefined)
      onChange?.("")
    }
  }

  const handleCalendarSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    if (date) {
      setInputValue(format(date, DISPLAY_FORMAT))
      setCalendarMonth(date)
      onChange?.(toISO(date))
      setOpen(false)
    } else {
      setInputValue("")
      onChange?.("")
    }
  }

  const isoValue = selectedDate ? toISO(selectedDate) : ""

  return (
    <>
      <input type="hidden" name={name} value={isoValue} aria-hidden="true" />
      <Popover
        open={open}
        onOpenChange={(nextOpen, eventDetails) => {
          // The text input isn't a registered PopoverTrigger, so Base UI's
          // outside-press dismiss logic doesn't recognize it as part of the
          // popover's anatomy. The click that focuses the input (which opens
          // the popover via onFocus below) gets misclassified as an outside
          // press once the dismiss listeners mount, closing it in the same
          // gesture. Veto only that specific case; real outside presses and
          // the icon trigger's own toggle are unaffected.
          if (!nextOpen && eventDetails.reason === "outside-press") {
            const target = eventDetails.event.target
            if (target instanceof Node && inputRef.current?.contains(target)) {
              eventDetails.cancel()
              return
            }
          }
          setOpen(nextOpen)
        }}
      >
        <div className="relative">
          <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="bday"
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedBy}
            aria-label={t("dateOfBirth")}
            className={cn(
              "flex h-10 w-full border border-input bg-transparent px-3 py-1.5 pr-10 text-base outline-none transition-colors",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50",
              "rounded-lg md:text-sm",
              className,
            )}
          />
          <PopoverTrigger
            render={<button type="button" />}
            disabled={disabled}
            className="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            aria-label={t("dateOfBirth")}
          >
            <Calendar className="h-4 w-4" aria-hidden="true" />
          </PopoverTrigger>
        </div>
        <PopoverContent
          anchor={inputRef}
          className="w-auto p-3"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            startMonth={min}
            endMonth={max}
            captionLayout="dropdown"
            locale={dateLocale}
            disabled={{ before: min, after: max }}
            showOutsideDays
            components={{
              Chevron: ({ orientation }) =>
                orientation === "left" ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                ),
            }}
          />
        </PopoverContent>
      </Popover>
    </>
  )
}
